import { sessionNoteMolecule } from '@web-app/atoms/in-session/sessionNoteMolecule';
import { Note } from '@web-app/server/api/types';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

export interface UseSessionNoteReturn {
  // Data
  sessionNote: Note | undefined;
  setSessionNote: (
    noteOrUpdater:
      | Note
      | undefined
      | ((prev: Note | undefined) => Note | undefined),
  ) => void;
  updateSessionNote: (updates: Partial<Note>) => void;
  resetSessionNote: () => void;

  // Query states
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;

  // Query controls
  refetch: () => Promise<void>;
}

/**
 * Hook to access and update the session note during in-call experience with built-in fetching.
 *
 * This hook owns fetching logic via Jotai atoms + React Query integration.
 * The roomId is automatically derived from the SessionScope.
 *
 * Used by CallV2 TipTapTab for provider notes during the call.
 *
 * Note: For post-session charting with AI-generated content, use useSessionNoteWithAi instead.
 *
 * Example usage:
 * ```ts
 * const { sessionNote, isLoading } = useSessionNote();
 * // Automatically fetches based on current session
 * ```
 */
export function useSessionNote(): UseSessionNoteReturn {
  const {
    sessionNoteAtom,
    sessionNoteLoadingAtom,
    sessionNoteFetchingAtom,
    sessionNoteErrorAtom,
    resetSessionNoteAtom,
    refetchSessionNoteAtom,
  } = useMolecule(sessionNoteMolecule);

  const [sessionNote, setSessionNoteAtom] = useAtom(sessionNoteAtom);
  const isLoading = useAtomValue(sessionNoteLoadingAtom);
  const isFetching = useAtomValue(sessionNoteFetchingAtom);
  const error = useAtomValue(sessionNoteErrorAtom) ?? null;
  const reset = useSetAtom(resetSessionNoteAtom);
  const refetch = useSetAtom(refetchSessionNoteAtom);

  // Wrapper to support both direct value and function updater patterns
  const setSessionNote = useCallback(
    (
      noteOrUpdater:
        | Note
        | undefined
        | ((prev: Note | undefined) => Note | undefined),
    ) => {
      if (typeof noteOrUpdater === 'function') {
        setSessionNoteAtom((prev) => noteOrUpdater(prev));
      } else {
        setSessionNoteAtom(noteOrUpdater);
      }
    },
    [setSessionNoteAtom],
  );

  const updateSessionNote = useCallback(
    (updates: Partial<Note>) => {
      setSessionNoteAtom((prev) => {
        // Handle sessionNote merging logic
        let nextSessionNote = prev?.sessionNote;

        if ('sessionNote' in updates) {
          if (updates.sessionNote === null) {
            // Explicitly setting to null
            nextSessionNote = null;
          } else if (updates.sessionNote) {
            // Merging with existing data
            nextSessionNote = {
              ...prev?.sessionNote,
              ...updates.sessionNote,
            };
          }
          // If updates.sessionNote is undefined, preserve existing
        }

        return {
          ...prev,
          ...updates,
          sessionNote: nextSessionNote,
        } as Note;
      });
    },
    [setSessionNoteAtom],
  );

  return {
    sessionNote,
    setSessionNote,
    updateSessionNote,
    resetSessionNote: reset,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook that returns a stable function reading the current session note on demand.
 *
 * Unlike useSessionNote, it does not subscribe the caller to the note atom, so
 * the calling component does not re-render on every note change (e.g. each editor keystroke).
 * Use it when the note is only needed inside event handlers.
 *
 * Example usage:
 * ```ts
 * const getSessionNote = useGetSessionNote();
 * const onSave = () => save(getSessionNote());
 * ```
 */
export function useGetSessionNote(): () => Note | undefined {
  const { sessionNoteAtom } = useMolecule(sessionNoteMolecule);
  return useAtomCallback(
    useCallback((get) => get(sessionNoteAtom), [sessionNoteAtom]),
  );
}
